// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xHasRole } from "@tps/App/Store/Selectors/GeneralSelectors";
import { USER_ROLES } from "@tps/XDL/constants";
import { fontConfig } from "@tps/XDL/PreStyle/typography";
import { mergeWithCss } from "@tps/XDL/Utilities";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    extendedStyles?: ProductListProductEmployeePartNumbersStyles;
}

type Props = OwnProps;

export interface ProductListProductEmployeePartNumbersStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    modelNumberWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    productNumber?: TypographyPresentationProps;
}

export const productEmployeePartNumbersStyles: ProductListProductEmployeePartNumbersStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-bottom: 4px;
        `,
    },
    modelNumberWrapper: {
        css: css`
            display: flex;
            gap: 4px;
        `,
    },
    label: {
        weight: fontConfig.weight.bold,
        ...fontConfig.xSmall,
        css: css`
            &::after {
                content: ":";
            }
        `,
    },
    value: {
        ...fontConfig.xSmall,
    },
    productNumber: {
        ...fontConfig.xSmall,
    },
};

const ProductEmployeePartNumbers = ({ product, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeWithCss(productEmployeePartNumbersStyles, extendedStyles));

    const isEmployee = useAppSelector(state => xHasRole(state, USER_ROLES.EMPLOYEE));

    return (
        <StyledWrapper {...styles.container} data-test-selector="product_partNumbers">
            {isNonEmptyString(product.detail?.modelNumber) && (
                <StyledWrapper {...styles.modelNumberWrapper}>
                    <Typography {...styles.label}>{translate("Model #")}</Typography>
                    <Typography {...styles.value}>{product.detail?.modelNumber}</Typography>
                </StyledWrapper>
            )}
            {isEmployee && <Typography {...styles.productNumber}>{product.productNumber}</Typography>}
        </StyledWrapper>
    );
};

export default ProductEmployeePartNumbers;
